import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { darken, lighten } from "@material-ui/core/styles/colorManipulator";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import LinearProgress from "@material-ui/core/LinearProgress";
import MuiTextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import {
  XGrid,
  type GridColumns,
  type GridSelectionModel,
} from "@material-ui/x-grid";

import DialogTitle from "src/components/DialogTitleWithClose";
import BrandField from "src/components/BrandField";
import VendorField from "src/components/VendorField";
import TextField, {
  NumberFieldOnBlur as NumberField,
} from "src/components/TextFieldBlur";
import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import type { PendingInventory } from "src/apiService/modules/pendingInventory";
import { asDate } from "src/utils/date";
import { formatDate } from "src/utils";
import {
  PendingInventoryGridHeaderHeight,
  usePendingInventoryGridStyles,
} from "src/pages/RootDialogs/pendingInventoryGridStyles";

type PendingEdit = Omit<Partial<PendingInventory>, "purchase_date"> & {
  purchase_date?: Date | null;
};

const CopyKeys = [
  "sku",
  "purchase_price",
  "purchase_date",
  "brand",
  "location",
  "vendor",
] as const;

/** Brand/SKU are only copied into a row that does not have one yet. */
const CopyOnlyIfEmptyKeys: readonly (typeof CopyKeys)[number][] = [
  "sku",
  "brand",
];

const CopyButtonTooltip =
  "Click to copy the Purchase Date, Purchase Price, Location, and Vendor from the prior row. If there’s no Brand or SKU in the current row, these values will also be copied.";

const ColumnWidth = {
  itemTitle: { flex: 1, minWidth: 325 },
  quantity: 80,
  sku: 120,
  listDate: 100,
  purchasePrice: 120,
  purchaseDate: 210,
  brand: 176,
  location: 120,
  vendor: 200,
  actions: 88,
} as const;

/**
 * Inputs, autocompletes, and the row's action buttons own their clicks.
 * Everything else in the row toggles selection.
 */
function isInteractiveRowTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || typeof element.closest !== "function") return false;
  return !!element.closest(
    "input, textarea, select, button, a, [role='button'], .MuiAutocomplete-root"
  );
}

/**
 * Whole-number quantity input. Keeps a draft so rejected characters snap back
 * instead of leaving an invalid value in the DOM. 0 is allowed to pass through
 * so Add can flag it; negative numbers and decimals are rejected.
 */
function QuantityField({
  value,
  disabled,
  onCommit,
}: {
  value?: number;
  disabled: boolean;
  onCommit: (quantity: number) => void;
}) {
  const [draft, setDraft] = useState(() =>
    value == null ? "" : String(value)
  );

  useEffect(() => {
    setDraft(value == null ? "" : String(value));
  }, [value]);

  return (
    <MuiTextField
      variant="outlined"
      margin="dense"
      type="number"
      disabled={disabled}
      value={draft}
      inputProps={{ min: 1, step: 1, inputMode: "numeric" }}
      onChange={(event) => {
        const raw = event.target.value;
        if (raw === "") {
          setDraft("");
          onCommit(0);
          return;
        }
        const quantity = Number(raw);
        if (!Number.isInteger(quantity) || quantity < 0) {
          setDraft(value == null ? "" : String(value));
          return;
        }
        setDraft(raw);
        onCommit(quantity);
      }}
    />
  );
}

const useStyles = makeStyles((theme) => ({
  gridFields: {
    "& .MuiFormControl-root.MuiFormControl-marginDense": {
      marginTop: 0,
      marginBottom: 0,
    },
    "& .MuiFormControl-root, & .MuiAutocomplete-root": {
      width: "100%",
    },
    // Cells set an inline line-height of rowHeight. Autocomplete inputs
    // inherit it and grow past the row, so the field sits off-center.
    "& .MuiAutocomplete-root, & .MuiAutocomplete-input": {
      lineHeight: "normal",
    },
    // XGrid inherits `box-sizing: border-box` to every descendant, overriding
    // MUI InputBase's content-box sizing. That collapses the Autocomplete
    // input's padding into its 19px height, making Brand/Vendor 31px instead
    // of the 40px used by the other fields. Restore content-box so the
    // Autocomplete keeps its natural 40px height.
    "& .MuiAutocomplete-input": {
      boxSizing: "content-box",
    },
    "& .MuiAutocomplete-inputRoot": {
      alignItems: "center",
      flexWrap: "nowrap",
    },
    // Match plain outlined fields to the Brand/Vendor autocomplete height.
    "& .MuiOutlinedInput-root:not(.MuiAutocomplete-inputRoot)": {
      height: 40,
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-root:not(.MuiAutocomplete-inputRoot) .MuiOutlinedInput-input":
      {
        paddingTop: 0,
        paddingBottom: 0,
        height: "100%",
        boxSizing: "border-box",
      },
    "& .MuiInputAdornment-root": {
      height: 24,
      maxHeight: 24,
    },
    "& .MuiOutlinedInput-adornedEnd .MuiIconButton-root": {
      padding: 4,
    },
  },
  title: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    whiteSpace: "normal",
    lineHeight: 1.25,
    width: "100%",
  },
  editor: {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
    width: "100%",
    maxHeight: "100%",
    lineHeight: "normal",
  },
  tooltipWrapper: {
    display: "inline-flex",
  },
  actions: {
    justifyContent: "space-between",
  },
  selectedCount: {
    alignSelf: "center",
    marginRight: theme.spacing(2),
    whiteSpace: "nowrap",
  },
  loadProgress: {
    flex: "1 1 auto",
    maxWidth: 280,
    minWidth: 140,
    marginRight: theme.spacing(2),
  },
  loadProgressLabel: {
    marginBottom: theme.spacing(0.5),
  },
  loadProgressBar: {
    height: 6,
    borderRadius: 3,
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  // MUI Alert "error" standard-variant colors, applied to rows whose quantity
  // still needs to be set. Repeated `&` outranks the grid's own row/selected
  // background rules.
  invalidQuantityRow: {
    "&&&&": {
      backgroundColor: lighten(theme.palette.error.main, 0.9),
      color: darken(theme.palette.error.main, 0.6),
    },
    "&&&&:hover": {
      backgroundColor: lighten(theme.palette.error.main, 0.85),
    },
    "&&&&.Mui-selected, &&&&.Mui-selected:hover": {
      backgroundColor: lighten(theme.palette.error.main, 0.9),
    },
    "&&&& .MuiDataGrid-cell": {
      color: "inherit",
    },
  },
}));

export default function PendingInventoryImport({
  rows,
  loading,
  hasMore,
  loadedCount,
  totalCount,
  onLoadMore,
  onClose,
  onPatch,
  onDelete,
  onAdd,
}: {
  rows: PendingInventory[];
  loading: boolean;
  hasMore: boolean;
  loadedCount: number;
  totalCount: number | null;
  onLoadMore: () => void;
  onClose: () => void;
  onPatch: (id: string, patch: PendingEdit) => void;
  onDelete: (ids: string[]) => void;
  onAdd: (ids: string[], options?: { close?: boolean }) => Promise<void>;
}) {
  const classes = useStyles();
  const gridClasses = usePendingInventoryGridStyles();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [adding, setAdding] = useState(false);
  // Rows the reviewer tried to add while their quantity was still 0. They stay
  // selected and get the MUI error background until the quantity is fixed.
  const [invalidQuantityIds, setInvalidQuantityIds] = useState<string[]>([]);
  // Add owns the grid until its flush + promote sequence finishes: show the
  // loading state and block every editor/action so no edit can be queued
  // after the Add dispatch.
  const busy = loading || adding;
  const onPatchRef = useRef(onPatch);
  const onDeleteRef = useRef(onDelete);
  const rowsRef = useRef(rows);
  onPatchRef.current = onPatch;
  onDeleteRef.current = onDelete;
  rowsRef.current = rows;

  const selectedRows = rows.filter(
    (row) => row.id && selectionModel.includes(row.id)
  );
  const missingPurchaseDate = selectedRows.some((row) => !row.purchase_date);
  const validQuantityRows = selectedRows.filter(
    (row) => Number(row.quantity) > 0
  );
  const invalidQuantityRows = selectedRows.filter(
    (row) => !(Number(row.quantity) > 0)
  );

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: "item_title",
        headerName: "Item Title",
        flex: ColumnWidth.itemTitle.flex,
        minWidth: ColumnWidth.itemTitle.minWidth,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <span className={classes.title} title={params.row.item_title || ""}>
            {params.row.item_title}
          </span>
        ),
      },
      {
        field: "quantity",
        headerName: "Qty",
        width: ColumnWidth.quantity,
        minWidth: ColumnWidth.quantity,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const id = String(params.id);
          return (
            <QuantityField
              value={params.row.quantity}
              disabled={adding}
              onCommit={(quantity) => {
                onPatchRef.current(id, { quantity });
                if (quantity > 0) {
                  setInvalidQuantityIds((ids) =>
                    ids.filter((value) => value !== id)
                  );
                }
              }}
            />
          );
        },
      },
      {
        field: "list_date",
        headerName: "List Date",
        width: ColumnWidth.listDate,
        minWidth: ColumnWidth.listDate,
        sortable: false,
        filterable: false,
        // Firestore hands dates back as Timestamp objects, which formatDate
        // cannot parse directly (the column used to render blank).
        valueGetter: (params) =>
          formatDate(asDate(params.row.list_date), "MM/DD/yyyy", timeZone) ||
          "",
      },
      {
        field: "purchase_date",
        headerName: "Purchase Date*",
        width: ColumnWidth.purchaseDate,
        minWidth: ColumnWidth.purchaseDate,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ZonedKeyboardDatePicker
            disableToolbar
            variant="inline"
            inputVariant="outlined"
            format="MM/dd/yyyy"
            margin="dense"
            autoOk
            required
            disabled={adding}
            value={asDate(params.row.purchase_date) || null}
            onChange={(date) =>
              onPatchRef.current(String(params.id), {
                purchase_date: date || null,
              })
            }
            KeyboardButtonProps={{ "aria-label": "change date" }}
          />
        ),
      },
      {
        field: "purchase_price",
        headerName: "Purchase Price*",
        width: ColumnWidth.purchasePrice,
        minWidth: ColumnWidth.purchasePrice,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <NumberField
            variant="outlined"
            margin="dense"
            disabled={adding}
            value={params.row.purchase_price}
            onChange={(event) =>
              onPatchRef.current(String(params.id), {
                purchase_price: +(event.target.value || 0),
              })
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        ),
      },
      {
        field: "sku",
        headerName: "SKU",
        width: ColumnWidth.sku,
        minWidth: ColumnWidth.sku,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <TextField
            variant="outlined"
            margin="dense"
            disabled={adding}
            value={params.row.sku || ""}
            onChange={(event) =>
              onPatchRef.current(String(params.id), { sku: event.target.value })
            }
          />
        ),
      },
      {
        field: "brand",
        headerName: "Brand",
        width: ColumnWidth.brand,
        minWidth: ColumnWidth.brand,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <div className={classes.editor}>
            <BrandField
              value={params.row.brand || ""}
              disabled={adding}
              onChange={(_event, value) =>
                onPatchRef.current(String(params.id), { brand: value || "" })
              }
              TextFieldProps={{ variant: "outlined", margin: "dense" }}
            />
          </div>
        ),
      },
      {
        field: "location",
        headerName: "Location",
        width: ColumnWidth.location,
        minWidth: ColumnWidth.location,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <TextField
            variant="outlined"
            margin="dense"
            disabled={adding}
            value={params.row.location || ""}
            onChange={(event) =>
              onPatchRef.current(String(params.id), {
                location: event.target.value,
              })
            }
          />
        ),
      },
      {
        field: "vendor",
        headerName: "Vendor",
        width: ColumnWidth.vendor,
        minWidth: ColumnWidth.vendor,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <div className={classes.editor}>
            <VendorField
              label={null}
              margin="dense"
              disabled={adding}
              value={params.row.vendor}
              onChange={(vendor) =>
                onPatchRef.current(String(params.id), { vendor: vendor || "" })
              }
            />
          </div>
        ),
      },
      {
        field: "actions",
        headerName: " ",
        width: ColumnWidth.actions,
        minWidth: ColumnWidth.actions,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => null,
        renderCell: (params) => {
          const currentRows = rowsRef.current;
          const index = currentRows.findIndex((row) => row.id === params.id);
          const previous = index > 0 ? currentRows[index - 1] : undefined;
          const canCopy =
            !!previous &&
            ((Number(previous.purchase_price) || 0) > 0 ||
              !!previous.purchase_date ||
              !!previous.location ||
              !!previous.vendor ||
              CopyOnlyIfEmptyKeys.some(
                (key) => !params.row[key] && !!previous[key]
              ));
          return (
            <Box display="flex">
              <Tooltip title={CopyButtonTooltip}>
                <span className={classes.tooltipWrapper}>
                  <IconButton
                    size="small"
                    className="inventory-import-copy-button"
                    disabled={!canCopy || adding}
                    onClick={() => {
                      const latest = rowsRef.current;
                      const latestIndex = latest.findIndex(
                        (row) => row.id === params.id
                      );
                      const source =
                        latestIndex > 0 ? latest[latestIndex - 1] : undefined;
                      if (!source) return;
                      const patch: PendingEdit = {};
                      for (const key of CopyKeys) {
                        if (
                          CopyOnlyIfEmptyKeys.includes(key) &&
                          params.row[key]
                        ) {
                          continue;
                        }
                        if (source[key]) patch[key] = source[key] as never;
                      }
                      patch.purchase_price = patch.purchase_price || 0;
                      if (!params.row.brand) patch.brand = source.brand || "";
                      patch.location = patch.location || "";
                      onPatchRef.current(String(params.id), patch);
                    }}
                  >
                    <FileCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                size="small"
                className={classes.deleteIcon}
                disabled={adding}
                onClick={() => onDeleteRef.current([String(params.id)])}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [
      adding,
      classes.deleteIcon,
      classes.editor,
      classes.title,
      classes.tooltipWrapper,
      timeZone,
    ]
  );

  const addTooltip =
    !busy && selectedRows.length === 0
      ? "Select the items you want to add"
      : missingPurchaseDate
      ? "Enter a purchase date for each selected item"
      : "";

  return (
    <>
      <DialogTitle onClose={onClose}>Inventory Import</DialogTitle>
      <DialogContent
        dividers
        id="pending-inventory-import-grid"
        className={gridClasses.content}
      >
        <XGrid
          className={clsx(gridClasses.grid, classes.gridFields)}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          getRowClassName={(params) =>
            invalidQuantityIds.includes(String(params.id))
              ? classes.invalidQuantityRow
              : ""
          }
          loading={busy}
          checkboxSelection
          disableColumnFilter
          disableColumnMenu
          hideFooter
          density="standard"
          rowHeight={50}
          headerHeight={PendingInventoryGridHeaderHeight}
          selectionModel={selectionModel}
          onSelectionModelChange={(model) => {
            if (!adding) setSelectionModel(model);
          }}
          // Keep the grid's own click handler off so a click inside an input
          // does not toggle selection; row clicks are handled below.
          disableSelectionOnClick
          onCellClick={(_params, event) => {
            if (
              adding ||
              _params.field === "__check__" ||
              isInteractiveRowTarget(event.target)
            ) {
              return;
            }
            const id = String(_params.id);
            setSelectionModel((previous) =>
              previous.includes(id)
                ? previous.filter((selectedId) => selectedId !== id)
                : [...previous, id]
            );
          }}
          scrollEndThreshold={200}
          onRowsScrollEnd={() => {
            if (!hasMore || busy) return;
            onLoadMore();
          }}
        />
      </DialogContent>
      <DialogActions
        className={
          totalCount != null && totalCount > 0 ? classes.actions : undefined
        }
      >
        {totalCount != null && totalCount > 0 ? (
          <Box className={classes.loadProgress}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.loadProgressLabel}
            >
              {loadedCount.toLocaleString()} of {totalCount.toLocaleString()}{" "}
              loaded
            </Typography>
            <LinearProgress
              className={classes.loadProgressBar}
              variant="determinate"
              value={Math.min(100, (loadedCount / totalCount) * 100)}
            />
          </Box>
        ) : null}
        <Box display="flex" alignItems="center" marginLeft="auto">
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.selectedCount}
          >
            {selectedRows.length.toLocaleString()} selected
          </Typography>
          <div
            id="inventory-import-actions"
            className="MuiDialogActions-spacing"
          >
            <Button
              color="secondary"
              variant="contained"
              className={classes.deleteButton}
              disabled={busy || selectedRows.length === 0}
              onClick={() =>
                onDelete(selectedRows.map((row) => row.id as string))
              }
            >
              Delete selected
            </Button>
            <Tooltip title={addTooltip}>
              <span>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={
                    busy || selectedRows.length === 0 || missingPurchaseDate
                  }
                  onClick={() => {
                    if (adding) return;
                    const invalidIds = invalidQuantityRows.map(
                      (row) => row.id as string
                    );
                    const validIds = validQuantityRows.map(
                      (row) => row.id as string
                    );
                    setAdding(true);
                    void (async () => {
                      try {
                        if (validIds.length) {
                          await onAdd(validIds, {
                            close: invalidIds.length === 0,
                          });
                        }
                      } catch {
                        // onAdd reports its own error.
                      } finally {
                        setAdding(false);
                        if (invalidIds.length) {
                          setInvalidQuantityIds(invalidIds);
                          setSelectionModel(invalidIds);
                          toast.error(
                            "An item with quantity 0 cannot be added, please set the correct quantity."
                          );
                        }
                      }
                    })();
                  }}
                >
                  Add
                </Button>
              </span>
            </Tooltip>
          </div>
        </Box>
      </DialogActions>
    </>
  );
}
